var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Observer } from 'mobx-react-lite';
import React from 'react';
import { ContextColor } from '../../constants/color.enum';
import { CLOCK } from '../../controllers/util-level/clock.controller';
import joinClassName from '../../utils/className.utils';
import { getContextColorStyle } from '../../utils/color.utils';
import { useProps, useStore } from '../../utils/mobx.utils';
import { createMomentFn } from '../../utils/time.utils';
import TimeDisplayer from '../TimeDisplayer/TimeDisplayer';
import './BaseCalendarEvent.scss';
import BaseCalendarEventDefaultTitleRenderer from './BaseCalendarEventDefaultTitleRenderer';
function BaseCalendarEvent(props) {
    var _a;
    var p = useProps(props);
    var _createMoment = createMomentFn((_a = p.timezoneMode) !== null && _a !== void 0 ? _a : 'auto');
    var s = useStore(function () { return ({
        get nowMoment() {
            return CLOCK.localNowMoment;
        },
        get event() {
            return p.calendarEvent;
        },
        get TitleRenderer() {
            return p.titleRenderer || BaseCalendarEventDefaultTitleRenderer;
        },
        get title() {
            return React.createElement(s.TitleRenderer, { calendarEvent: s.event });
        },
        get startTime() {
            return s.event.startTime;
        },
        get startTimeMoment() {
            return s.event.startTime ? _createMoment(s.event.startTime) : null;
        },
        get startTimeDisplay() {
            return s.startTimeMoment ? s.startTimeMoment.format('HH:mm') : '--:--';
        },
        get endTime() {
            return s.event.endTime;
        },
        get endTimeMoment() {
            return s.event.endTime ? _createMoment(s.event.endTime) : null;
        },
        get endTimeDisplay() {
            return s.endTimeMoment ? s.endTimeMoment.format('HH:mm') : '--:--';
        },
        get isInThePast() {
            if (s.endTimeMoment)
                return s.endTimeMoment.isBefore(s.nowMoment);
            else if (s.startTimeMoment)
                return s.startTimeMoment.isBefore(s.nowMoment);
            return null;
        },
        get isDisabled() {
            return s.event.isDisabled;
        },
        get hasEventTypeGroupsDefined() {
            return p.eventTypeGroups.length > 0;
        },
        get belongsToEventTypeGroups() {
            return p.eventTypeGroups.filter(function (g) { return g.groupTypeChecker ? g.groupTypeChecker(s.event) : true; });
        },
        get belongsToEventTypes() {
            return s.belongsToEventTypeGroups.map(function (g) { return g.eventTypes; }).flat().filter(function (et) { return et.typeChecker ? et.typeChecker(s.event) : true; });
        },
        get firstActiveEventType() {
            return s.belongsToEventTypes.find(function (t) { return t.isActive; });
        },
        get shouldRender() {
            return !s.event.hidden && (!s.hasEventTypeGroupsDefined || (s.firstActiveEventType || this.belongsToEventTypes.some(function (t) { return t.isActive; })));
        },
        get shouldShowTimestamps() {
            return p.showTimestamps !== undefined ? p.showTimestamps : true;
        },
        handleComponentClick: function (e) {
            var _a;
            if (s.isDisabled)
                return;
            typeof ((_a = p.calendarEvent.componentProps) === null || _a === void 0 ? void 0 : _a.onClick) === 'function' && p.calendarEvent.componentProps.onClick();
            typeof p.defaultOnEventClick === 'function' && p.defaultOnEventClick(s.event);
        },
        get style() {
            var _a, _b, _c;
            return __assign(__assign({}, p.style), getContextColorStyle(ContextColor.Primary, (_b = (_a = p.color) !== null && _a !== void 0 ? _a : s.event.color) !== null && _b !== void 0 ? _b : (_c = s.firstActiveEventType) === null || _c === void 0 ? void 0 : _c.color));
        }
    }); });
    return React.createElement(Observer, { children: function () {
            var _a;
            return React.createElement("div", { className: joinClassName('BaseCalendarEvent', p.className, (_a = p.calendarEvent.componentProps) === null || _a === void 0 ? void 0 : _a.className, s.isDisabled && 'disabled', s.isInThePast && 'inThePast'), "data-event-id": p.calendarEvent.id, "data-hidden": !s.shouldRender, style: s.style, onClick: s.handleComponentClick },
                s.shouldShowTimestamps && React.createElement("div", { className: "BaseCalendarEventTimeWrapper" },
                    React.createElement(TimeDisplayer, { value: s.event.startTime, timezoneMode: p.timezoneMode }),
                    p.shouldShowEventEndTime && s.endTime && (React.createElement(React.Fragment, null,
                        React.createElement("em", null, "to"),
                        React.createElement(TimeDisplayer, { value: s.event.endTime, timezoneMode: p.timezoneMode })))),
                React.createElement("div", { className: "BaseCalendarEventContent" },
                    !props.children && React.createElement("header", { className: "BaseCalendarEventHeader" },
                        React.createElement("h3", { className: "BaseCalendarEventTitle" }, s.title)),
                    props.children && React.createElement("div", { className: "BaseCalendarEventBody" }, props.children)));
        } });
}
export default BaseCalendarEvent;
